import { createSlice } from "@reduxjs/toolkit";

import {
  addTerminalUpdate,
  editTerminalUpdate,
  fetchTerminalUpdatesList,
  removeTerminalUpdate,
} from "../thunks/terminalUpdatesThunks";

import type { TerminalUpdate } from "../../utils/types/terminalUpdates";

interface TerminalUpdatesState {
  list: TerminalUpdate[];
  loading: boolean;
  error: string | null;
}

const initialState: TerminalUpdatesState = {
  list: [],
  loading: false,
  error: null,
};

const isSameActiveScope = (a: TerminalUpdate, b: TerminalUpdate) =>
  a.companyId === b.companyId &&
  a.portCallingsId === b.portCallingsId &&
  a.severity === b.severity;

const replaceTerminalUpdateInScope = (
  list: TerminalUpdate[],
  newRecord: TerminalUpdate,
  oldId?: string
) => [
  newRecord,
  ...list.filter(
    (item) =>
      item.terminalUpdateId !== oldId &&
      item.terminalUpdateId !== newRecord.terminalUpdateId &&
      !isSameActiveScope(item, newRecord)
  ),
];

export const terminalUpdatesSlice = createSlice({
  name: "terminalUpdates",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch
    builder
      .addCase(fetchTerminalUpdatesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTerminalUpdatesList.fulfilled, (state, action) => {
        state.list = action.payload;
        state.loading = false;
      })
      .addCase(fetchTerminalUpdatesList.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });

    // Add
    builder
      .addCase(addTerminalUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTerminalUpdate.fulfilled, (state, action) => {
        state.loading = false;
        state.list = replaceTerminalUpdateInScope(state.list, action.payload);
      })
      .addCase(addTerminalUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Edit
    builder
      .addCase(editTerminalUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editTerminalUpdate.fulfilled, (state, action) => {
        state.loading = false;
        const { oldId, newRecord } = action.payload;
        state.list = replaceTerminalUpdateInScope(state.list, newRecord, oldId);
      })
      .addCase(editTerminalUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Remove
    builder
      .addCase(removeTerminalUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeTerminalUpdate.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(
          (item) => item.terminalUpdateId !== action.payload.id
        );
      })
      .addCase(removeTerminalUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default terminalUpdatesSlice.reducer;
